/**
 * Public invoice payment page (Iter-59).
 *
 * Anyone with the invoice id can land here — no login required. Shows the
 * outstanding balance + a Pay Now button that redirects to Stripe Checkout
 * (using the band's BYO Stripe key). After Stripe redirects back, polls
 * /api/invoices/{id}/pay/status until the backend confirms 'paid'.
 *
 * Mounted at /invoices/:id/pay outside the protected Layout.
 */

import { useEffect, useState, useCallback } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, AlertTriangle, CreditCard, Loader2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { API } from "../lib/api";

// Bare axios — this page is public, never sends a token.
const pub = axios.create({ baseURL: API });

const POLL_INTERVAL_MS = 2000;
const POLL_MAX_ATTEMPTS = 8;

function formatCurrency(amount, currency) {
  try {
    return new Intl.NumberFormat("en-GB", { style: "currency", currency: (currency || "GBP").toUpperCase() }).format(amount);
  } catch {
    return `${(currency || "GBP").toUpperCase()} ${Number(amount || 0).toFixed(2)}`;
  }
}

export default function InvoicePay() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const sessionIdFromUrl = params.get("session_id");
  const cancelled = params.get("cancelled") === "1";

  const [invoice, setInvoice] = useState(null);
  const [error, setError] = useState(null);
  const [creating, setCreating] = useState(false);
  const [polling, setPolling] = useState(false);
  const [paid, setPaid] = useState(false);

  // Fetch a public preview of the invoice — backend exposes /invoices/{id}/public
  // which only includes the customer-safe fields (no internal notes etc.).
  useEffect(() => {
    pub.get(`/invoices/${id}/public`).then(
      (r) => setInvoice(r.data),
      (e) => setError(e.response?.data?.detail || "Could not load invoice")
    );
  }, [id]);

  const checkStatus = useCallback(async (sessId) => {
    try {
      const r = await pub.get(`/invoices/${id}/pay/status?session_id=${encodeURIComponent(sessId)}`);
      if (r.data.payment_status === "paid") {
        setPaid(true);
        setPolling(false);
        return true;
      }
      return false;
    } catch {
      return false;
    }
  }, [id]);

  // If we returned from Stripe with a session_id, poll until paid (max ~16s)
  useEffect(() => {
    if (!sessionIdFromUrl) return;
    let active = true;
    setPolling(true);
    let attempts = 0;
    const tick = async () => {
      if (!active) return;
      attempts += 1;
      const done = await checkStatus(sessionIdFromUrl);
      if (done || attempts >= POLL_MAX_ATTEMPTS) {
        setPolling(false);
        return;
      }
      setTimeout(tick, POLL_INTERVAL_MS);
    };
    tick();
    return () => { active = false; };
  }, [sessionIdFromUrl, checkStatus]);

  const startPayment = async () => {
    setCreating(true);
    setError(null);
    try {
      const r = await pub.post(`/invoices/${id}/pay/checkout`, {
        origin_url: window.location.origin,
      });
      window.location.href = r.data.url;
    } catch (e) {
      setError(e.response?.data?.detail || "Could not start payment");
      setCreating(false);
    }
  };

  // Render states
  if (error) {
    return (
      <Shell>
        <div className="text-center space-y-3">
          <AlertTriangle className="w-10 h-10 text-amber-400 mx-auto" />
          <h1 className="text-xl font-heading">{error}</h1>
          <p className="text-sm text-muted-foreground">If you think this is a mistake, please contact your group's treasurer.</p>
        </div>
      </Shell>
    );
  }

  if (!invoice) {
    return (
      <Shell>
        <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading invoice…
        </div>
      </Shell>
    );
  }

  if (paid || invoice.status === "paid") {
    return (
      <Shell>
        <div className="text-center space-y-4">
          <CheckCircle2 className="w-14 h-14 text-emerald-400 mx-auto" />
          <h1 className="text-2xl font-heading">Payment received — thank you!</h1>
          <p className="text-muted-foreground">
            Invoice <span className="font-mono">{invoice.invoice_number}</span> has been marked as paid.
            A receipt will be emailed shortly.
          </p>
        </div>
      </Shell>
    );
  }

  return (
    <Shell>
      <div className="space-y-6">
        <div className="text-center">
          <p className="overline">{invoice.band_name}</p>
          <h1 className="font-heading text-3xl mt-1">Invoice {invoice.invoice_number}</h1>
          <p className="text-sm text-muted-foreground mt-1">For {invoice.recipient_name}</p>
        </div>

        <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-6 text-center">
          <p className="overline">Amount due</p>
          <p className="text-5xl font-heading mt-1" data-testid="invoice-public-amount">
            {formatCurrency(invoice.balance, invoice.currency)}
          </p>
          {invoice.due_date && (
            <p className="text-xs text-muted-foreground mt-2">
              Due by{" "}
              {new Date(invoice.due_date).toLocaleDateString("en-GB", {
                day: "2-digit", month: "long", year: "numeric",
              })}
            </p>
          )}
        </div>

        {Array.isArray(invoice.line_items) && invoice.line_items.length > 0 && (
          <ul className="text-sm divide-y divide-white/5 rounded-lg border border-white/5">
            {invoice.line_items.map((li, i) => (
              <li key={i} className="flex items-center justify-between p-3">
                <span>{li.description}</span>
                <span className="font-mono text-muted-foreground">
                  {formatCurrency((li.quantity || 1) * (li.unit_price || 0), invoice.currency)}
                </span>
              </li>
            ))}
          </ul>
        )}

        {cancelled && !sessionIdFromUrl && (
          <p className="text-sm text-amber-400 text-center">Payment cancelled. You can try again below.</p>
        )}
        {polling && (
          <p className="text-sm text-muted-foreground text-center flex items-center justify-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" /> Confirming with Stripe…
          </p>
        )}

        <Button
          onClick={startPayment}
          disabled={creating || polling}
          className="w-full h-12 bg-emerald-500 hover:bg-emerald-600 text-white font-semibold"
          data-testid="invoice-public-pay-btn"
        >
          <CreditCard className="w-4 h-4 mr-2" />
          {creating ? "Redirecting to Stripe…" : `Pay ${formatCurrency(invoice.balance, invoice.currency)} now`}
        </Button>

        <p className="text-[11px] text-muted-foreground text-center">
          Payments are processed securely by Stripe. Your card details never touch our servers.
        </p>
      </div>
    </Shell>
  );
}

function Shell({ children }) {
  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-background">
      <div className="w-full max-w-lg rounded-3xl bg-card border border-white/10 p-8 shadow-2xl">
        {children}
      </div>
    </div>
  );
}
